import { useCallback, useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementsOptions } from '@stripe/stripe-js';

import type { ApplePayTheme } from 'types/applePay';
import type { PurchaseState } from 'types/checkout';
import type { PostPurchaseDataParams } from 'types/stripe';
import { cn } from 'helpers/classnames';
import { toStripePrice } from 'helpers/currency';
import { getStripe } from 'helpers/stripe';
import { ApplePayButton } from 'components/ApplePayButton';

interface ApplePayProps {
  isGuest: boolean;
  purchaseState: PurchaseState;
  theme?: ApplePayTheme;
  apiKey?: string;
  onReady?: () => void;
  onClose?: () => void;
  updatePurchase: (reqData: PostPurchaseDataParams) => Promise<PurchaseState>;
}

type StripeCardBrand = 'american_express' | 'discover_global_network' | 'mastercard' | 'visa';

type BetaStripeElementsOptions = StripeElementsOptions & {
  disallowedCardBrands: StripeCardBrand[];
};

export const ApplePay = ({ isGuest, theme = 'buy', purchaseState, apiKey, onReady, onClose, updatePurchase }: ApplePayProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [stripe, setStripe] = useState<Stripe | null>(null);

  const elementsOptions: BetaStripeElementsOptions = {
    amount: toStripePrice(purchaseState.chargeSummary.grandTotal),
    appearance: {},
    captureMethod: 'manual',
    currency: 'usd',
    disallowedCardBrands: ['american_express'],
    mode: 'payment',
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['card'],
    setupFutureUsage: 'off_session'
  };

  const hasValidAmount = elementsOptions.amount && elementsOptions.amount > 0;

  const initStripe = useCallback(async () => {
    if (!apiKey) return;

    const stripeInstance = await getStripe(apiKey);

    if (stripeInstance) {
      setStripe(stripeInstance);
    }
  }, [apiKey]);

  useEffect(() => {
    if (!stripe && apiKey) {
      initStripe();
    }
  }, [apiKey, stripe, initStripe]);

  const handleReady = () => {
    setIsLoading(false);
    onReady?.();
  };

  if (!hasValidAmount || !stripe) {
    return null;
  }

  return (
    <div data-testid="applePayContainer">
      {hasValidAmount && (
        <Elements stripe={stripe} options={elementsOptions}>
          <div className={cn({ flex: isLoading, hidden: !isLoading }, 'h-10 items-center justify-center')}>
            <div className="border-t-blue-600 relative top-1 h-5 w-5 animate-spin rounded-full border-2 border-gray-300" />
          </div>
          <ApplePayButton
            isGuest={isGuest}
            purchaseState={purchaseState}
            stripe={stripe}
            theme={theme}
            onReady={handleReady}
            onClose={onClose}
            updatePurchase={updatePurchase}
          />
        </Elements>
      )}
    </div>
  );
};
